import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast, ToastMessage } from "primereact/toast";
import { useTranslation } from "react-i18next";
import posService from "../../services/pos/pos.service";
import productService from "../../services/products/products.service";

interface AddProductStockDialogProps {
  posIds: number[];
  onSuccess: () => void;
}

interface POSOption {
  label: string;
  value: number;
  code: string;
  address: string;
}

interface ProductOption {
  label: string;
  value: number;
}

const AddProductStockDialog: React.FC<AddProductStockDialogProps> = ({ posIds, onSuccess }) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [selectedPOSIds, setSelectedPOSIds] = useState<number[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [safetyStock, setSafetyStock] = useState<number>(0);
  const [reorderStock, setReorderStock] = useState<number>(0);
  const [posOptions, setPosOptions] = useState<POSOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);


  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  useEffect(() => {
    loadPOSOptions();
    loadProductOptions();
  }, []);

  const loadPOSOptions = async () => {
    try {
      const response = await posService.getAllPOS();
      const mapped = response.map((pos: any) => ({
        label: `${pos.posCode}, ${pos.address2 || pos.cityName || "Unknown"}`,
        value: pos.posID,
        code: pos.posCode,
        address: pos.address2 || pos.address1 || "",
      }));
      setPosOptions(mapped);
    } catch (error) {
      console.error("Error loading POS options:", error);
      showMessage("Error", "Failed to load POS options", "error");
    }
  };

  const loadProductOptions = async () => {
    try {
      const response = await productService.getAllProducts();
      const mapped = (response || []).map((product: any) => ({
        label: product.name,
        value: product.id || product.productId,
      }));
      setProductOptions(mapped);
    } catch (error) {
      console.error("Error loading product options:", error);
      showMessage("Error", "Failed to load product options", "error");
    }
  };

  const validateForm = (): boolean => {
    if (!selectedPOSIds || selectedPOSIds.length === 0) {
      showMessage("Validation Error", "Please select at least one POS", "warn");
      return false;
    }

    if (!selectedProduct) {
      showMessage("Validation Error", "Please select a product", "warn");
      return false;
    }

    if (safetyStock < 0) {
      showMessage("Validation Error", "Safety Stock must be a positive value", "warn");
      return false;
    }

    if (reorderStock < 0) {
      showMessage("Validation Error", "Reorder Stock must be a positive value", "warn");
      return false;
    }

    return true;
  };

  const handleAddProduct = async () => {
    setShowValidation(true); 
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const payload = {
        posIds: selectedPOSIds,
        productId: selectedProduct,
        safetyStock: safetyStock,
        reorderStock: reorderStock,
      };

      const response = await posService.addProductStockConfig(payload);

      if (response?.isSuccess || response?.success) {
        showMessage("Success", "Product stock config added successfully", "success");
        setTimeout(() => {
          onSuccess();
        }, 1000);
      } else {
        showMessage("Error", response?.message || "Failed to add product stock config", "error");
      }
    } catch (error) {
      console.error("Error adding product stock config:", error);
      showMessage("Error", "An error occurred while adding product stock config", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedPOSIds([]);
    setSelectedProduct(null);
    setSafetyStock(0);
    setReorderStock(0);
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="p-fluid">
        <h4 className="mb-3">Add Product Stock Config</h4>

        {/* POS Multi-Select */}
        <div className="field mb-3">
          <label htmlFor="posIds" className="font-semibold text-sm block mb-1">
            POS <span className="text-red-500">*</span>
          </label>
          <MultiSelect
            id="posIds"
            value={selectedPOSIds}
            onChange={(e) => setSelectedPOSIds(e.value)}
            options={posOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Select POS locations"
            filter
            className="w-full border border-gray-300" 
            maxSelectedLabels={3}
            display="chip"
            appendTo="self" 
            panelStyle={{ width: '100%' }}  
          />
          {showValidation && selectedPOSIds.length === 0 && (
            <small className="p-error block mt-1">POS is required</small>
          )}
        </div>

        {/* Product Dropdown */}
        <div className="field mb-3">
          <label htmlFor="product" className="font-semibold text-sm block mb-1">
            Product <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="product"
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.value)}
            options={productOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Product"
            filter
            className="w-full border border-gray-300" 
            appendTo="self" 
            panelStyle={{ width: '100%' }} 
            
          />
          {showValidation && !selectedProduct && (
            <small className="p-error block mt-1">Product is required</small>
          )}
        </div>

        {/* Safety Stock */}
        <div className="field mb-3">
          <label htmlFor="safetyStock" className="font-semibold text-sm block mb-1">
            Safety Stock <span className="text-red-500">*</span>
          </label>
          <InputNumber
            id="safetyStock"
            value={safetyStock}
            onValueChange={(e) => setSafetyStock(e.value || 0)}
            placeholder="Enter safety stock"
            min={0}
            useGrouping={false}
            className="w-full"
            style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
          />
          {safetyStock < 0 && (
            <small className="p-error block mt-1">Safety Stock must be positive</small>
          )}
        </div>

        {/* Reorder Stock */}
        <div className="field mb-3">
          <label htmlFor="reorderStock" className="font-semibold text-sm block mb-1">
            Reorder Stock <span className="text-red-500">*</span>
          </label>
          <InputNumber
            id="reorderStock"
            value={reorderStock}
            onValueChange={(e) => setReorderStock(e.value || 0)}
            placeholder="Enter reorder stock"
            min={0}
            useGrouping={false}
            className="w-full"
            style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
          />
          {reorderStock > safetyStock && (
            <small className="p-error block mt-1">
              Reorder Stock cannot be greater than Safety Stock
            </small>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-2 justify-content-end mt-4">
          <Button
            label="Reset"
            onClick={resetForm}
            severity="secondary"
            text
            loading={loading}
          />
          <Button
            label="Add Product"
            onClick={handleAddProduct}
            loading={loading}
            disabled={loading}
          />
        </div>
      </div>
    </>
  );
};

export default AddProductStockDialog;
